import type { Socket } from 'socket.io-client'
import store from '../store'
import {
  setFriendRequestDeclinedNotice,
  setIncomingFriendRequest,
} from './state/pvp/reducer'
import type {
  PvpFriendRequestDeclinedNotice,
  PvpIncomingFriendRequest,
} from './state/pvp/state'
import { postPvpFriendPendingIncoming } from './api'

type FriendSocketHandlers = {
  onIncoming: (data: PvpIncomingFriendRequest) => void
  onCancelled: (data: { requestId?: string }) => void
  onDeclined: (data: PvpFriendRequestDeclinedNotice) => void
}

const handlersBySocket = new WeakMap<Socket, FriendSocketHandlers>()

/** Minimum gap between `pending-incoming` API calls for the same wallet (unless forced). */
const HYDRATE_MIN_INTERVAL_MS = 30_000

let hydrateInFlight: Promise<void> | null = null
let lastHydrateWallet = ''
let lastHydrateAt = 0

export type HydrateIncomingFriendRequestOptions = {
  /** Bypass throttle (initial load, socket reconnect). */
  force?: boolean
}

/**
 * Sync Redux from `POST /pvp/friends/pending-incoming`.
 * Throttled and deduped so multiple mounts / sockets do not spam the API.
 */
export async function hydrateIncomingFriendRequest(
  walletAddress: string,
  options?: HydrateIncomingFriendRequestOptions,
): Promise<void> {
  const wallet = walletAddress.trim()
  if (!wallet) return

  const walletKey = wallet.toLowerCase()
  const now = Date.now()
  const force = options?.force === true

  if (
    !force &&
    walletKey === lastHydrateWallet &&
    now - lastHydrateAt < HYDRATE_MIN_INTERVAL_MS
  ) {
    return
  }

  if (hydrateInFlight) {
    return hydrateInFlight
  }

  hydrateInFlight = (async () => {
    try {
      lastHydrateAt = Date.now()
      lastHydrateWallet = walletKey

      const res = await postPvpFriendPendingIncoming({ walletAddress: wallet })
      if (!res.success) return

      if (!res.requests?.length) {
        if (store.getState().app.pvp.incomingFriendRequest) {
          store.dispatch(setIncomingFriendRequest(null))
        }
        return
      }

      const first = res.requests[0]
      store.dispatch(
        setIncomingFriendRequest({
          requestId: first.requestId,
          requesterWallet: first.requesterWallet,
          requesterNickname: first.requesterNickname,
          requesterAvatarUrl: first.requesterAvatarUrl,
          requesterUserLvl: first.requesterUserLvl,
          requesterPvpMmr: first.requesterPvpMmr,
          createdAtUnixMs: first.createdAtUnixMs,
        }),
      )
    } finally {
      hydrateInFlight = null
    }
  })()

  return hydrateInFlight
}

/**
 * Listen for PvP friend requests on any game socket (land GlobalAttackAlert or PvP shared socket).
 * Uses store.dispatch so handlers never go stale when React effects re-run.
 */
export function attachFriendRequestSocketListeners(socket: Socket): void {
  detachFriendRequestSocketListeners(socket)

  const onIncoming = (data: PvpIncomingFriendRequest) => {
    if (!data?.requestId || !data?.requesterWallet) return
    store.dispatch(setIncomingFriendRequest(data))
  }

  const onCancelled = (data: { requestId?: string }) => {
    if (!data?.requestId) return
    const incoming = store.getState().app.pvp.incomingFriendRequest
    if (incoming?.requestId === data.requestId) {
      store.dispatch(setIncomingFriendRequest(null))
    }
  }

  const onDeclined = (data: {
    requestId?: string
    recipientWallet?: string
    recipientNickname?: string
  }) => {
    const recipientWallet = String(data?.recipientWallet || '').trim().toLowerCase()
    if (!recipientWallet) return
    const recipientNickname =
      String(data?.recipientNickname || '').trim() ||
      `${recipientWallet.slice(0, 6)}…${recipientWallet.slice(-4)}`
    store.dispatch(
      setFriendRequestDeclinedNotice({
        requestId: data?.requestId,
        recipientWallet,
        recipientNickname,
      }),
    )
  }

  handlersBySocket.set(socket, { onIncoming, onCancelled, onDeclined })
  socket.on('pvp:friend-request-received', onIncoming)
  socket.on('pvp:friend-request-cancelled', onCancelled)
  socket.on('pvp:friend-request-declined', onDeclined)
}

export function detachFriendRequestSocketListeners(socket: Socket | null): void {
  if (!socket) return
  const handlers = handlersBySocket.get(socket)
  if (handlers) {
    socket.off('pvp:friend-request-received', handlers.onIncoming)
    socket.off('pvp:friend-request-cancelled', handlers.onCancelled)
    socket.off('pvp:friend-request-declined', handlers.onDeclined)
    handlersBySocket.delete(socket)
  }
}
